/**
 * Toasts.
 *
 *   const { addToast } = useToast();
 *   addToast({ message: "Key created", type: "success" });
 *   addToast({ message: "Couldn't save", type: "error" });             errors stay 8s, success and info 4s
 *   addToast({ message: "Exported", type: "success", duration: 6000 });
 *
 *   import { toast } from "@/context/ToastContext";                    outside React (services, lib/copy.ts)
 *   toast.success("Copied"); toast.error("Couldn't copy"); toast.info("Batch queued");
 *
 * Toasts pause while hovered or focused, stack bottom-right (full width with a 16px gutter on phones), sit above
 * dialogs, and are announced by a live region (errors assertively). Use them for results of an action the user
 * just took; don't toast that a page "loaded successfully".
 */
import { createContext, useContext, useState, ReactNode, useCallback, useMemo, useRef, useEffect } from "react";

export type ToastType = "success" | "error" | "info";

export type Toast = {
  id: string;
  message: string;
  type?: ToastType;
  duration?: number;
};

type ToastContextType = {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, "id">) => string;
  removeToast: (id: string) => void;
  pauseToast: (id: string) => void;
  resumeToast: (id: string) => void;
};

export const DEFAULT_DURATION: Record<ToastType, number> = { success: 4000, info: 4000, error: 8000 };
const MAX_VISIBLE = 4;

const ToastContext = createContext<ToastContextType | undefined>(undefined);

// Lets code outside React raise a toast through the mounted provider.
let globalAdd: ((t: Omit<Toast, "id">) => string) | null = null;
export const toast = {
  show: (message: string, type: ToastType = "info", duration?: number) => globalAdd?.({ message, type, duration }),
  success: (message: string, duration?: number) => globalAdd?.({ message, type: "success", duration }),
  error: (message: string, duration?: number) => globalAdd?.({ message, type: "error", duration }),
  info: (message: string, duration?: number) => globalAdd?.({ message, type: "info", duration }),
};

const newId = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  // Per-toast timer bookkeeping so hover can pause and resume with the remaining time.
  const timers = useRef(new Map<string, { handle: ReturnType<typeof setTimeout> | null; remaining: number; startedAt: number }>());

  const removeToast = useCallback((id: string) => {
    const t = timers.current.get(id);
    if (t?.handle) clearTimeout(t.handle);
    timers.current.delete(id);
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const start = useCallback(
    (id: string, ms: number) => {
      const handle = setTimeout(() => removeToast(id), ms);
      timers.current.set(id, { handle, remaining: ms, startedAt: Date.now() });
    },
    [removeToast]
  );

  const addToast = useCallback(
    (toast: Omit<Toast, "id">) => {
      const id = newId();
      const type = toast.type ?? "info";
      const duration = toast.duration ?? DEFAULT_DURATION[type];
      setToasts(prev => {
        // Same message already showing: restart it instead of stacking duplicates.
        const dup = prev.find(t => t.message === toast.message && t.type === type);
        if (dup) {
          const old = timers.current.get(dup.id);
          if (old?.handle) clearTimeout(old.handle);
          timers.current.delete(dup.id);
        }
        const next = [...prev.filter(t => t !== dup), { ...toast, type, duration, id }];
        return next.slice(-MAX_VISIBLE);
      });
      start(id, duration);
      return id;
    },
    [start]
  );

  const pauseToast = useCallback((id: string) => {
    const t = timers.current.get(id);
    if (!t || !t.handle) return;
    clearTimeout(t.handle);
    t.remaining = Math.max(1000, t.remaining - (Date.now() - t.startedAt));
    t.handle = null;
  }, []);

  const resumeToast = useCallback(
    (id: string) => {
      const t = timers.current.get(id);
      if (!t || t.handle) return;
      start(id, t.remaining);
    },
    [start]
  );

  useEffect(() => {
    globalAdd = addToast;
    const map = timers.current;
    return () => {
      if (globalAdd === addToast) globalAdd = null;
      map.forEach(t => t.handle && clearTimeout(t.handle));
    };
  }, [addToast]);

  const value = useMemo(
    () => ({ toasts, addToast, removeToast, pauseToast, resumeToast }),
    [toasts, addToast, removeToast, pauseToast, resumeToast]
  );

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) throw new Error("useToast must be used within ToastProvider");
  return context;
};
